/**
 * AURA - Accessibility, User Responsiveness, and Assistance
 * Main Entry Point
 * 
 * Usage:
 * <script src="https://aura.nbsintranet.com/v1/widget.iife.js" data-app-id="my-app" async defer></script>
 */

import { engine, AuraAPI } from './core/engine';
import { Logger } from './core/logger';

const logger = new Logger('Main');

declare global {
  interface Window {
    Aura?: {
      config?: Record<string, unknown>;
      api?: AuraAPI;
    };
  }
}

/**
 * Wait for document.body to be available, with a timeout fallback.
 * Handles edge cases where DOMContentLoaded fires but body is still null.
 */
function waitForBody(timeoutMs = 5000): Promise<void> {
  if (document.body) return Promise.resolve();

  return new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      logger.warn('Timed out waiting for document.body');
      resolve();
    }, timeoutMs);

    const observer = new MutationObserver(() => {
      if (document.body) {
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      }
    });

    observer.observe(document.documentElement, { childList: true });
  });
}

async function init(): Promise<void> {
  logger.info('AURA widget loading...');

  try {
    if (document.readyState === 'loading') {
      await new Promise<void>((resolve) => {
        document.addEventListener('DOMContentLoaded', () => resolve(), {
          once: true,
        });
      });
    }

    await waitForBody();

    const api = await engine.init();

    window.Aura = window.Aura || {};
    window.Aura.api = api;

    logger.info('AURA widget initialized successfully');
  } catch (error) {
    logger.error('Failed to initialize AURA widget', error);
  }
}

// Prevent any stray async error originating from widget code from
// becoming an unhandled rejection on the host page.
try {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const msg = reason instanceof Error ? reason.message : String(reason ?? '');
    if (msg.includes('AURA') || msg.includes('aura')) {
      event.preventDefault();
      logger.error('Suppressed unhandled rejection from widget', reason);
    }
  });
} catch (_) { /* swallow */ }

// Auto-initialize -- the promise is explicitly void-returned so that
// any rejection is caught by the internal try/catch above, never by
// the host page's global unhandledrejection handler.
void init();

export { engine };
export type { AuraAPI };
export * from './core/types';
